import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import PageSection from 'components/page/page-section/PageSection';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import OfferDeliveryDetails from 'modules/subcampaigns/components/offer-delivery-details/OfferDeliveryDetails';
import { withModal } from 'components/modal/ModalHOC';
import EditShippingModal, { MODE_BUSINESS_DETAILS_ADD } from 'modules/shipping/components/edit-shipping-modal/EditShippingModal';

import {
  hideBusinessAndBankDetailsConfirmModal,
  showBusinessAndBankDetailsConfirmModal
} from 'modules/influencers-agency/reducers/influencers-agencies-reducer';

import { __t, applyTranslations } from 'utils/language';
import { shouldInfluencerFillBusinessDetails } from 'utils/organization';

import type {
  OrganizationSettingsForm,
  ShippingAddress,
  State
} from 'types';

import './offer-shipping-info.scss';

applyTranslations(require('./offer-shipping-info.i18n.json'));

type OwnProps = {
  isOfferAccepted: boolean,
  isForBrand: boolean,
  subcampaignId: number,
  includesDelivery: boolean,
  shippingAddress: ShippingAddress | undefined | null,
  influencerOrganizationSettings: OrganizationSettingsForm,
  onToggled: (a: boolean) => void
};

type StateProps = {
  isTalentAgency: boolean
};

type DispatchProps = {
  dispatchNotifyThatEditingIsNotAnOption: () => void
};

type Props = StateProps & DispatchProps & OwnProps;

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: EditShippingModal
});

const OfferShippingInfo = (
  {
    shippingAddress,
    includesDelivery,
    subcampaignId,
    isForBrand,
    isOfferAccepted,
    isTalentAgency,
    influencerOrganizationSettings,
    onToggled,
    dispatchNotifyThatEditingIsNotAnOption
  }: Props
): JSX.Element => {
  const [isExpanded, setExpanded] = React.useState(false);
  const expandButtonClassnames = classnames('offer-shipping-info__expand-button', {
    'offer-shipping-info__expand-button--expanded': isExpanded
  });

  let title = includesDelivery
    ? __t('offer_shipping_info.title_includes_shipping')
    : __t(`offer_shipping_info.title_${isExpanded ? 'expanded' : 'collapsed'}`);

  if (!isOfferAccepted) {
    title = __t(`offer_shipping_info.${isForBrand ? 'brand' : 'creator'}_title_not_agreed`);
  }

  const tooltipMessage = __t('offer_shipping_info.tooltip');

  const hasBusinessDetails = influencerOrganizationSettings && !shouldInfluencerFillBusinessDetails(influencerOrganizationSettings);

  const toggleExpand = () => {
    if (!isOfferAccepted) {
      return;
    }

    const nextIsExpanded = !isExpanded;
    setExpanded(nextIsExpanded);
    onToggled(nextIsExpanded);
  };

  const renderBusinessDetailsBanner = () => (
    <div className="offer-shipping-info__banner">
      <div className="offer-shipping-info__banner-icon">
        <SVGDynamicIcon
          name="exclamation-inside-circle-custom-bg-white-fg"
          width={16}
          height={16}
          customColor="red"
        />
      </div>
      <div className="offer-shipping-info__banner-title">
        {__t('offer_shipping_info.business_details_are_missing')}
        {isForBrand ? '' : __t('offer_shipping_info.business_details_need_to_provide')}
      </div>
      {
        !isForBrand && isTalentAgency && (
          <ActionButton
            className="offer-shipping-info__button"
            width="minimum"
            onClick={dispatchNotifyThatEditingIsNotAnOption}
          >
            {__t('offer_shipping_info.add_business_details')}
          </ActionButton>
        )
      }
      {
        !isForBrand && !isTalentAgency && (
          <ActionButtonWithModal
            className="offer-shipping-info__button"
            width="minimum"
            modalContentProps={{
              subcampaignId,
              shippingAddress: shippingAddress ?? undefined, // replace possible null with undefined
              mode: MODE_BUSINESS_DETAILS_ADD
            }}
          >
            {__t('offer_shipping_info.add_business_details')}
          </ActionButtonWithModal>
        )
      }
    </div>
  );

  return (
    <PageSection
      style="volume"
      padding="none"
      margin="none"
      border="none"
    >
      <div className="offer-shipping-info">
        <div className="offer-shipping-info__content">
          <span>
            {title}
          </span>
          <div className="offer-shipping-info__expander">
            <div className="offer-shipping-info__address-icon">
              <Tooltip
                message={tooltipMessage}
              >
                <SVGDynamicIcon
                  name="address-book-custom-fg"
                  width={24}
                  height={24}
                  customColor="grey"
                  clickable
                  disabled={!isOfferAccepted}
                  onClick={toggleExpand}
                />
              </Tooltip>
            </div>
            <div
              className={expandButtonClassnames}
              onClick={toggleExpand}
            >
              <Tooltip
                message={tooltipMessage}
              >
                <SVGDynamicIcon
                  name="chevron-down-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                  clickable
                  disabled={!isOfferAccepted}
                />
              </Tooltip>
            </div>
          </div>
        </div>
        {isOfferAccepted && !hasBusinessDetails && renderBusinessDetailsBanner()}
        {
          isOfferAccepted && isExpanded && (
            <OfferDeliveryDetails
              isForBrand={isForBrand}
              subcampaignId={subcampaignId}
              includesDelivery={includesDelivery}
              shippingAddress={shippingAddress}
              organizationSettings={influencerOrganizationSettings}
            />
          )
        }
      </div>
    </PageSection>
  );
};

const mapStateToProps = ({ organizations }: State): StateProps => ({
  isTalentAgency: organizations.currentOrganization?.type === 'influencer'
    && !isNil(organizations.currentOrganization.agencyOrganizationId)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchNotifyThatEditingIsNotAnOption:
    () => dispatch(showBusinessAndBankDetailsConfirmModal(
      () => dispatch(hideBusinessAndBankDetailsConfirmModal()))
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(OfferShippingInfo);
